// Self-inflicted capture_items LWW-conflict detection (the >30-PUT batch-wedge
// ROOT; memory put-batch-wedge-prod).
//
// A failed_precondition whose authoritative row ALREADY equals this device's
// current local intent is NOT a real cross-device conflict — it is the device
// re-sending its OWN already-applied edit after a sibling 429 forced the
// all-or-nothing CRUD batch to retry (a >30-PUT page can't ack under the per-op
// PUT rate limit). handleLwwConflict (connector-dispatch-item-patch.ts) uses this
// to ACK such a conflict instead of reapplying, because reapplying rewrites the
// SAME values (a value no-op) → PowerSync's onlyWhenChanged drops sync_version
// from the re-queued op → the next PATCH omits syncVersion → Encore decode 400
// "missing field syncVersion" → rethrow → the WHOLE device queue wedges forever.
//
// Extracted from connector-dispatch-item-patch.ts (the 500-line lint:max-lines
// cap) and exported so it can be unit-tested directly against the value/scale
// edge cases without standing up the full dispatch path.

import type { PowerSyncWriteDb } from './powersync-db'

import { quantitiesEqual } from './connector-quantity-equal'
import { canonicalizeQuantity } from './quantity-canonical'

/**
 * True when the `failed_precondition` authoritative row ALREADY holds this
 * device's current local intent — i.e. the conflict is SELF-INFLICTED, not a
 * real cross-device divergence. Compares the persisted local `capture_items`
 * row (the surveyor's latest intent — the optimistic write stored the FULL row,
 * not the thin opData) against the authoritative quantity / is_zero_confirmed /
 * notes. Quantity goes through `quantitiesEqual` so the server's SCALED
 * numeric(10,2) ('6.00') matches the local unscaled '6' (the #780 scale gap) — a
 * bare String compare would miss every self-inflicted conflict and re-wedge.
 * Missing local row → false (take the normal reapply path). sync_version is NOT
 * compared: a value match is the safe ACK signal, and a converged higher-version
 * row self-heals via the sync-down stream. See memory put-batch-wedge-prod.
 */
export async function authoritativeMatchesLocalIntent(
  db: PowerSyncWriteDb,
  id: string,
  auth: Record<string, unknown>,
): Promise<boolean> {
  const rows = await db.getAll<{
    quantity: unknown
    is_zero_confirmed: unknown
    notes: unknown
  }>(
    `SELECT quantity, is_zero_confirmed, notes
       FROM capture_items WHERE id = ? LIMIT 1`,
    [id],
  )
  const local = rows[0]
  if (local === undefined) return false
  // Canonicalise the local row's quantity before the value-compare: a device
  // wedged BEFORE this fix shipped still holds a raw draft ("5.") in its local
  // row, and canonicalDecimal("5.") is null → quantitiesEqual would fall back to
  // a string compare ("5.00" !== "5.") and mis-classify a self-inflicted conflict
  // as genuine, firing a needless reapply on every batch retry (audit-1 #5).
  const localQuantity =
    canonicalizeQuantity(String(local.quantity)) ?? local.quantity
  if (!quantitiesEqual(auth['quantity'], localQuantity)) return false
  const authZero = auth['isZeroConfirmed'] === true
  const localZero =
    local.is_zero_confirmed === 1 || local.is_zero_confirmed === true
  if (authZero !== localZero) return false
  const authNotes = auth['notes'] ?? null
  const localNotes = local.notes ?? null
  return authNotes === localNotes
}
